import * as React from "react";
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/lib/auth/AuthProvider";
import { Toaster } from "@/components/ui/toaster";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { BuildVersionFooter } from "@/components/BuildVersionFooter";
import Login from "@/routes/public/Login";
import NotFound from "@/routes/public/NotFound";
import Pricing from "@/routes/public/Pricing";
import Privacy from "@/routes/public/Privacy";
import Terms from "@/routes/public/Terms";
import TrafficRanking from "@/routes/public/TrafficRanking";

const CHUNK_RELOAD_KEY = "nextsub:chunk-reload";

/**
 * Lazy-load with a one-shot full reload on dynamic import failure.
 * After a Pages deploy, an open tab may keep an old index that references
 * replaced asset hashes; reloading picks up the new HTML/chunk map.
 */
function lazyWithChunkReload<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return mod;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|error loading dynamically imported module/i.test(
          message,
        );
      if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        // Suspend forever while the page reloads.
        return new Promise(() => undefined);
      }
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw err;
    }
  });
}

// The authenticated area is a separate lazy chunk; anonymous visitors and
// edge-cached static pages never download it.
const AppRoot = lazyWithChunkReload(() => import("@/routes/app/AppRoot"));

function AppFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      {t("common.loading")}
    </div>
  );
}

function RouteErrorPage() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold">{t("common.somethingWentWrong")}</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        {t("common.pageLoadFailed")}
      </p>
      <button
        type="button"
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
          window.location.assign("/app");
        }}
      >
        {t("common.reloadApp")}
      </button>
    </div>
  );
}
function RootLayout() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/app")) {
    return <Outlet />;
  }
  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col">
        <Outlet />
      </div>
      <BuildVersionFooter />
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <Login /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/terms", element: <Terms /> },
      { path: "/login", element: <Login /> },
      { path: "/rank", element: <TrafficRanking /> },
      {
        path: "/app/*",
        errorElement: <RouteErrorPage />,
        element: (
          <React.Suspense fallback={<AppFallback />}>
            <AppRoot />
          </React.Suspense>
        ),
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
